import React, { useState, useMemo, useEffect } from "react";
import config from "../config.js";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { apiErrorMessage } from '../utils/apiError';
import { amadaSizeText } from '../utils/sheetGrain';
import { mapsSummary, partMapFor, uploadSheetMaps } from '../features/cutSheets/nestMapUpload';

const API_BASE = config.API_BASE_URL;

/**
 * Amada Multi-Sheet Import Modal
 *
 * Workflow:
 *   1. Owner picker + PDF file upload → POST /cut_sheets/parse_amada_report?owner_customer_id=X
 *   2. Review: one card per parsed program. Each card shows:
 *        - Diagram + data-name thumbnail
 *        - Editable cut_sheet_name (pre-filled from Claude OCR)
 *        - Material dropdown pre-selected with top match_candidate
 *        - Parts table with per-row part dropdown pre-selected with top match_candidate
 *        - Confidence badges (green 1.0, yellow 0.7+, red <0.7)
 *        - Exclude checkbox
 *   3. Dry Run → POST /cut_sheets/batch_create with dry_run=true (validates)
 *   4. Import → POST /cut_sheets/batch_create (commits)
 *   5. Done → show created cut_sheet_ids, user can close
 *
 * Props:
 *   show          boolean, modal visibility
 *   onClose       callback when user cancels / closes
 *   allCustomers  pre-loaded list of { customer_id, name } for the owner picker
 *   allMaterials  pre-loaded list of materials for the override dropdown
 *   allParts      pre-loaded list of parts for the override dropdown
 *   onImported    optional callback(createdIds) fired after successful commit so the
 *                 parent page can refresh its cut-sheet list
 */
export default function AmadaImportModal({
    show,
    onClose,
    allCustomers = [],
    allMaterials = [],
    allParts = [],
    onImported,
}) {
    // --- Step state ---
    const [step, setStep] = useState("upload"); // upload | parsing | review | importing | done | error
    const [errorMsg, setErrorMsg] = useState(null);

    // --- Upload step state ---
    // ownerCustomerId  = WHO OWNS THE RAW MATERIAL. Filters material match
    //                    candidates to that customer's inventory (KanCraft
    //                    when we supply our own sheet, the customer when
    //                    they ship-in material).
    // cutSheetCustomerId = WHO THE CUT SHEET (and the parts on it) IS FOR.
    //                    Auto-mirrors `ownerCustomerId` until the operator
    //                    explicitly picks something different — common
    //                    case: KanCraft-owned sheet stock used to cut
    //                    Boyd-owned parts.
    const [ownerCustomerId, setOwnerCustomerId] = useState("");
    const [cutSheetCustomerId, setCutSheetCustomerId] = useState("");
    const [pdfFile, setPdfFile] = useState(null);

    // --- Review step state ---
    // parseResult: { programs: [...], warnings: [...] }
    const [parseResult, setParseResult] = useState(null);
    // editedPrograms[idx] = {
    //   excluded: bool,
    //   cut_sheet_name: string,
    //   material_id: number | null,
    //   parts: { [partIdx]: { part_id: number | null } }
    // }
    const [edited, setEdited] = useState({});

    // --- Import step state ---
    const [dryRunOk, setDryRunOk] = useState(false);
    const [createdIds, setCreatedIds] = useState([]);
    // Result of the commit, including the revisions[] summary the backend
    // returns so the Done step can report "FOO Rev A → Rev B".
    const [importResult, setImportResult] = useState(null);
    // Sheet maps (the PDF kept with each cut sheet so the laser kiosk can draw it): null until tried.
    const [mapResults, setMapResults] = useState(null);

    // nameConflicts: { [trimmedName]: { existing_cut_sheet_id,
    //   current_revision_code, next_revision_code } } for every proposed name
    // that already exists as a current revision. Populated by check_names at
    // the Review step. A program is "in conflict" when its trimmed name is a
    // key here; the operator resolves it by renaming or by checking the
    // "import as new revision" box on that card.
    const [nameConflicts, setNameConflicts] = useState({});

    // --- Reset state each time the modal is opened ---
    useEffect(() => {
        if (!show) return;
        setStep("upload");
        setErrorMsg(null);
        setOwnerCustomerId("");
        setCutSheetCustomerId("");
        setPdfFile(null);
        setParseResult(null);
        setEdited({});
        setDryRunOk(false);
        setCreatedIds([]);
        setImportResult(null);
        setMapResults(null);
        setNameConflicts({});
    }, [show]);

    // Auto-mirror Material Owner → Cut Sheet Customer until the operator
    // explicitly picks something different. After they diverge once, the
    // cut sheet customer is sticky and changing the material owner won't
    // overwrite it. (`""` = empty = auto-mirror still active.)
    useEffect(() => {
        setCutSheetCustomerId(prev => (prev === "" ? ownerCustomerId : prev));
    }, [ownerCustomerId]);

    // --- Helper: confidence → badge class ---
    function confidenceBadge(conf) {
        if (conf >= 0.99) return "bg-success";
        if (conf >= 0.7)  return "bg-warning text-dark";
        return "bg-danger";
    }

    // --- Step 1: parse_amada_report ---
    const handleParse = async () => {
        if (!ownerCustomerId) { setErrorMsg("Select a material owner first."); return; }
        if (!pdfFile) { setErrorMsg("Choose a PDF file first."); return; }
        setErrorMsg(null);
        setStep("parsing");
        try {
            const fd = new FormData();
            fd.append("file", pdfFile);
            const url = `${API_BASE}/cut_sheets/parse_amada_report?owner_customer_id=${encodeURIComponent(ownerCustomerId)}`;
            const res = await fetch(url, { method: "POST", body: fd });
            if (!res.ok) {
                throw new Error(await apiErrorMessage(res, `Parse failed (${res.status})`));
            }
            const data = await res.json();
            if (!data.programs || data.programs.length === 0) {
                throw new Error("No cut sheet programs were parsed from this PDF. Check that it is an Amada Sheet Report.");
            }
            setParseResult(data);

            // Seed the edited state with top-match defaults for each program
            const initialEdited = {};
            data.programs.forEach((prog, idx) => {
                const topMat = (prog.material_match_candidates || [])[0];
                const partsEdits = {};
                (prog.parts || []).forEach((p, pIdx) => {
                    const topPart = (p.match_candidates || [])[0];
                    partsEdits[pIdx] = {
                        part_id: topPart ? topPart.part_id : null,
                        quantity_per_sheet: p.qty_per_sheet || 0,
                    };
                });
                // Seed remnants array. Preferred: parser's `remnants` array.
                // Fallback: a single-element array from the legacy scalars
                // (older API responses). Each entry stores values as strings
                // so partial edits don't trigger validation until save.
                let remnantsSeed = [];
                if (Array.isArray(prog.remnants) && prog.remnants.length > 0) {
                    remnantsSeed = prog.remnants.map(r => ({
                        width:    r.width != null ? r.width.toString() : "",
                        length:   r.length != null ? r.length.toString() : "",
                        quantity: r.quantity != null ? r.quantity.toString() : "1",
                        detected: true,  // came from parser
                    }));
                } else if (prog.remnant_width != null && prog.remnant_length != null) {
                    remnantsSeed = [{
                        width:    prog.remnant_width.toString(),
                        length:   prog.remnant_length.toString(),
                        quantity: (prog.remnant_quantity != null ? prog.remnant_quantity : 1).toString(),
                        detected: true,
                    }];
                }

                initialEdited[idx] = {
                    excluded: false,
                    cut_sheet_name: prog.cut_sheet_name || "",
                    material_id: topMat ? topMat.material_id : null,
                    parts: partsEdits,
                    customParts: [],  // operator-added rows when the parser missed something
                    remnants: remnantsSeed,
                };
            });
            setEdited(initialEdited);
            setStep("review");
        } catch (err) {
            console.error("Parse error:", err);
            setErrorMsg(err.message || String(err));
            setStep("error");
        }
    };

    // --- Step 2/3: build batch payload from edited state ---
    const buildBatchPayload = (dryRun = false) => {
        const sheets = [];
        const maps = [];    // per sheet, in the same order: the program's pages + the import's part matches
        parseResult.programs.forEach((prog, idx) => {
            const e = edited[idx];
            if (!e || e.excluded) return;

            // Build parts list — parser-detected rows (skipping any the operator
            // marked as removed) + operator-added custom rows.
            const parts = [];
            (prog.parts || []).forEach((p, pIdx) => {
                const partEdit = e.parts[pIdx];
                if (!partEdit || partEdit.removed) return;
                const chosen = partEdit.part_id;
                if (chosen) {
                    const editedQty = partEdit.quantity_per_sheet;
                    const qty = (editedQty !== undefined && editedQty !== null && editedQty !== "")
                        ? parseInt(editedQty, 10)
                        : (p.qty_per_sheet || 0);
                    parts.push({
                        part_id: chosen,
                        quantity_per_sheet: qty,
                        customer_id: parseInt(cutSheetCustomerId),
                    });
                }
            });
            (e.customParts || []).forEach(cp => {
                if (!cp.part_id) return;
                const qty = parseInt(cp.quantity_per_sheet, 10);
                if (!Number.isFinite(qty) || qty < 1) return;
                parts.push({
                    part_id: cp.part_id,
                    quantity_per_sheet: qty,
                    customer_id: parseInt(cutSheetCustomerId),
                });
            });

            // Non-inventory remnants — drop incomplete rows so half-typed
            // entries don't 422 on Pydantic validation.
            const validRemnants = (e.remnants || [])
                .map(r => ({
                    width:    parseFloat(r.width),
                    length:   parseFloat(r.length),
                    quantity: parseInt(r.quantity, 10),
                }))
                .filter(r => Number.isFinite(r.width) && r.width > 0
                          && Number.isFinite(r.length) && r.length > 0
                          && Number.isFinite(r.quantity) && r.quantity >= 1);

            // If this name already exists and the operator chose to revise,
            // tag the sheet so the backend writes it as the NEXT revision of
            // the existing cut sheet (archiving the old one) using THIS
            // freshly-uploaded data.
            const nm = e.cut_sheet_name.trim();
            const conflict = nameConflicts[nm];
            const reviseOf = (conflict && e.reviseExisting)
                ? conflict.existing_cut_sheet_id
                : null;

            maps.push({ pages: prog.page_numbers || [], partMap: partMapFor(prog, e) });
            sheets.push({
                cut_sheet_name: nm,
                description: buildDescription(prog),
                ...(reviseOf ? { revision_of_cut_sheet_id: reviseOf } : {}),
                material_id: e.material_id,
                customer_id: parseInt(cutSheetCustomerId),
                cut_path_time: prog.cut_path_time || "00:00:00",
                sh_cut_time: prog.sh_cut_time || "00:00:00",
                pierce_time: prog.pierce_time || "00:00:00",
                total_cut_time: prog.total_cut_time || "00:00:00",
                shuttle_time: prog.shuttle_time || "00:02:40",
                image_path: prog.diagram_image_url || null,
                parts,
                // Multi-remnant array (preferred). Legacy scalars below
                // mirror the primary so old backends still see something
                // useful — the new backend reads `remnants` and ignores
                // them.
                remnants: validRemnants,
                remnant_width:    validRemnants[0] ? validRemnants[0].width    : null,
                remnant_length:   validRemnants[0] ? validRemnants[0].length   : null,
                remnant_quantity: validRemnants[0] ? validRemnants[0].quantity : null,
            });
        });
        return { payload: { sheets, dry_run: dryRun }, maps };
    };

    const buildDescription = (prog) => {
        const parts = [];
        if (prog.material_code) parts.push(prog.material_code);
        if (prog.sheet_length && prog.sheet_width) {
            parts.push(`${prog.sheet_length}x${prog.sheet_width}`);
        }
        if (prog.is_remnant) parts.push("Remnant");
        return parts.join(" ") || "Amada import";
    };

    // --- Step 2: dry run ---
    const handleDryRun = async () => {
        setErrorMsg(null);
        setDryRunOk(false);
        try {
            const { payload } = buildBatchPayload(true);
            if (payload.sheets.length === 0) {
                throw new Error("All sheets are excluded or have no valid parts.");
            }
            const res = await fetch(`${API_BASE}/cut_sheets/batch_create`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw new Error(await apiErrorMessage(res, `Dry run failed (${res.status})`));
            }
            const data = await res.json();
            if (!data.dry_run) {
                throw new Error("Dry run response missing dry_run flag — backend may be out of date.");
            }
            setDryRunOk(true);
        } catch (err) {
            console.error("Dry run error:", err);
            setErrorMsg(err.message || String(err));
        }
    };

    // --- Step 3: commit ---
    const handleImport = async () => {
        setErrorMsg(null);
        setStep("importing");
        try {
            const { payload, maps } = buildBatchPayload(false);
            const res = await fetch(`${API_BASE}/cut_sheets/batch_create`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw new Error(await apiErrorMessage(res, `Import failed (${res.status})`));
            }
            const data = await res.json();
            setCreatedIds(data.created_ids || []);
            setImportResult(data);
            setStep("done");
            // Keep the PDF with each new cut sheet (the laser kiosk's sheet map) BEFORE telling the page: the
            // page closes this window on onImported, which would hide the result and cut the uploads short.
            // Never fails the import.
            const ids = data.created_ids || [];
            if (pdfFile && ids.length === maps.length) {
                setMapResults("saving");
                const actor = sessionStorage.getItem("userName") || null;
                const results = await uploadSheetMaps(pdfFile, ids.map((id, i) => ({ cutSheetId: id, ...maps[i] })), actor)
                    .catch(() => []);
                setMapResults(results);
                const failed = results.filter(r => !r.ok || r.colour === false);
                if (failed.length) {
                    window.alert(`${mapsSummary(results)}\n` + failed.map(r => `Cut sheet ${r.cutSheetId}: ${
                        r.ok ? (r.problems || []).join("; ") : r.message}`).join("\n"));
                }
            }
            if (onImported) onImported(data.created_ids || []);
        } catch (err) {
            console.error("Import error:", err);
            setErrorMsg(err.message || String(err));
            setStep("review");
        }
    };

    // --- Per-program edit handlers ---
    const updateProgram = (idx, patch) => {
        setEdited(prev => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));
        // Any edit invalidates the dry-run OK state — user must re-run
        setDryRunOk(false);
    };
    const updateProgramPart = (idx, partIdx, patch) => {
        setEdited(prev => ({
            ...prev,
            [idx]: {
                ...prev[idx],
                parts: {
                    ...prev[idx].parts,
                    [partIdx]: { ...prev[idx].parts[partIdx], ...patch },
                },
            },
        }));
        setDryRunOk(false);
    };
    const removeProgramPart = (idx, partIdx) => {
        setEdited(prev => ({
            ...prev,
            [idx]: {
                ...prev[idx],
                parts: {
                    ...prev[idx].parts,
                    [partIdx]: { ...prev[idx].parts[partIdx], removed: true },
                },
            },
        }));
        setDryRunOk(false);
    };
    const restoreProgramPart = (idx, partIdx) => {
        setEdited(prev => ({
            ...prev,
            [idx]: {
                ...prev[idx],
                parts: {
                    ...prev[idx].parts,
                    [partIdx]: { ...prev[idx].parts[partIdx], removed: false },
                },
            },
        }));
        setDryRunOk(false);
    };
    const addCustomPart = (idx) => {
        setEdited(prev => ({
            ...prev,
            [idx]: {
                ...prev[idx],
                customParts: [
                    ...(prev[idx].customParts || []),
                    { part_id: null, quantity_per_sheet: 1 },
                ],
            },
        }));
        setDryRunOk(false);
    };
    const updateCustomPart = (idx, cIdx, patch) => {
        setEdited(prev => ({
            ...prev,
            [idx]: {
                ...prev[idx],
                customParts: (prev[idx].customParts || []).map(
                    (cp, i) => (i === cIdx ? { ...cp, ...patch } : cp)
                ),
            },
        }));
        setDryRunOk(false);
    };
    const removeCustomPart = (idx, cIdx) => {
        setEdited(prev => ({
            ...prev,
            [idx]: {
                ...prev[idx],
                customParts: (prev[idx].customParts || []).filter((_, i) => i !== cIdx),
            },
        }));
        setDryRunOk(false);
    };

    // --- Duplicate-name detection: ask the API which proposed names already
    // exist as a current revision, so we can warn + offer "create a revision".
    // Re-runs (debounced) whenever the set of included, non-empty names
    // changes while on the Review step. Renaming a sheet to something unique
    // clears its conflict automatically. ---
    const includedNamesKey = useMemo(() => {
        if (!parseResult) return "";
        const names = [];
        parseResult.programs.forEach((prog, idx) => {
            const e = edited[idx];
            if (!e || e.excluded) return;
            const nm = (e.cut_sheet_name || "").trim();
            if (nm) names.push(nm);
        });
        return JSON.stringify(Array.from(new Set(names)).sort());
    }, [parseResult, edited]);

    useEffect(() => {
        if (step !== "review") return;
        const names = JSON.parse(includedNamesKey || "[]");
        if (names.length === 0) { setNameConflicts({}); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const res = await fetch(`${API_BASE}/cut_sheets/check_names`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ names }),
                });
                if (!res.ok) return;  // soft-fail: backend guard still blocks on import
                const data = await res.json();
                if (cancelled) return;
                const map = {};
                (data.conflicts || []).forEach(c => { map[c.cut_sheet_name] = c; });
                setNameConflicts(map);
            } catch (_e) {
                /* network hiccup — leave prior conflicts, import-time guard backs us up */
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [includedNamesKey, step]);

    // A program is an unresolved conflict when its name exists AND the operator
    // has not opted to import it as a revision.
    const conflictForProgram = (idx) => {
        const e = edited[idx];
        if (!e || e.excluded) return null;
        const nm = (e.cut_sheet_name || "").trim();
        return nm ? (nameConflicts[nm] || null) : null;
    };

    // --- Import blockable if any included program is missing a material or any part ---
    const readyToImport = useMemo(() => {
        if (!parseResult) return false;
        let included = 0;
        for (let idx = 0; idx < parseResult.programs.length; idx++) {
            const e = edited[idx];
            if (!e || e.excluded) continue;
            included++;
            if (!e.cut_sheet_name || !e.cut_sheet_name.trim()) return false;
            if (!e.material_id) return false;
            // Name already exists → operator must either rename or explicitly
            // opt to import it as a new revision.
            const nm = e.cut_sheet_name.trim();
            if (nameConflicts[nm] && !e.reviseExisting) return false;
            const prog = parseResult.programs[idx];
            // Each parser-detected row that ISN'T removed must have a chosen part + valid qty.
            let activePartCount = 0;
            for (let pIdx = 0; pIdx < prog.parts.length; pIdx++) {
                const partEdit = e.parts[pIdx];
                if (!partEdit || partEdit.removed) continue;
                if (!partEdit.part_id) return false;
                const qty = parseInt(partEdit.quantity_per_sheet, 10);
                if (!Number.isFinite(qty) || qty < 1) return false;
                activePartCount++;
            }
            // Each operator-added custom row must have a chosen part + valid qty.
            for (const cp of (e.customParts || [])) {
                if (!cp.part_id) return false;
                const qty = parseInt(cp.quantity_per_sheet, 10);
                if (!Number.isFinite(qty) || qty < 1) return false;
                activePartCount++;
            }
            // At least one active part overall (parser-or-custom).
            if (activePartCount < 1) return false;
        }
        return included > 0;
    }, [parseResult, edited, nameConflicts]);

    // Parts dropdown options built from allParts
    const partOptions = useMemo(() => (
        (allParts || [])
            .filter(p => p.is_current_revision !== false && p.active_status !== false)
            .map(p => ({ value: p.part_id, label: `${p.product_id} — ${p.description || ""}` }))
            .sort((a, b) => a.label.localeCompare(b.label))
    ), [allParts]);

    // Materials dropdown, filtered to the selected owner
    const materialOptions = useMemo(() => (
        (allMaterials || [])
            .filter(m => !ownerCustomerId || m.owner_customer_id === parseInt(ownerCustomerId))
            .map(m => ({
                value: m.material_id,
                label: `${m.material_code} — ${m.description || ""}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    ), [allMaterials, ownerCustomerId]);

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl" style={{ maxWidth: "95vw" }}>
                <div className="modal-content" style={{ maxHeight: "95vh" }}>
                    <div className="modal-header bg-info-subtle">
                        <h5 className="modal-title">
                            <i className="bi bi-file-earmark-pdf me-2"></i>
                            Import Amada Sheet Report
                            {step === "review" && parseResult && (
                                <span className="badge bg-secondary ms-2">
                                    {parseResult.programs.length} cut sheet{parseResult.programs.length === 1 ? "" : "s"} parsed
                                </span>
                            )}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body overflow-auto">
                        {errorMsg && (
                            <div className="alert alert-danger">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {errorMsg}
                            </div>
                        )}

                        {/* ======================== UPLOAD STEP ======================== */}
                        {step === "upload" && (
                            <div>
                                <p className="text-muted">
                                    Upload a multi-page Amada Sheet Report PDF. Each cut sheet inside the PDF
                                    will be reviewed individually before import. Material candidates are scoped
                                    to the selected owner.
                                </p>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Material Owner <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-select"
                                        value={ownerCustomerId}
                                        onChange={e => setOwnerCustomerId(e.target.value)}
                                    >
                                        <option value="">-- Select owner --</option>
                                        {allCustomers.map(c => (
                                            <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                                        ))}
                                    </select>
                                    <div className="form-text">
                                        Material candidate matching is filtered to this customer's inventory.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Cut Sheet Customer <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-select"
                                        value={cutSheetCustomerId}
                                        onChange={e => setCutSheetCustomerId(e.target.value)}
                                    >
                                        <option value="">-- Select customer --</option>
                                        {allCustomers.map(c => (
                                            <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                                        ))}
                                    </select>
                                    <div className="form-text">
                                        Who the parts on this cut sheet are for. Defaults to the material
                                        owner — change it when you're cutting a customer's parts from
                                        KanCraft-supplied (or another customer's) sheet stock.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Amada PDF <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".pdf"
                                        onChange={e => setPdfFile(e.target.files[0] || null)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* ======================== PARSING STEP ======================== */}
                        {step === "parsing" && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary mb-3"></div>
                                <div>Parsing PDF and matching against JobQ catalog…</div>
                                <small className="text-muted">This usually takes 10–30 seconds.</small>
                            </div>
                        )}

                        {/* ======================== REVIEW STEP ======================== */}
                        {step === "review" && parseResult && (
                            <div>
                                {/* Warnings */}
                                {parseResult.warnings && parseResult.warnings.length > 0 && (
                                    <div className="alert alert-warning">
                                        <strong>
                                            <i className="bi bi-info-circle me-2"></i>
                                            {parseResult.warnings.length} parser warning{parseResult.warnings.length === 1 ? "" : "s"}
                                        </strong>
                                        <ul className="mb-0 mt-2 small">
                                            {parseResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                        </ul>
                                    </div>
                                )}

                                {/* Per-program cards */}
                                {parseResult.programs.map((prog, idx) => {
                                    const e = edited[idx] || {};
                                    const isExcluded = !!e.excluded;
                                    return (
                                        <div
                                            key={idx}
                                            className={`card mb-3 ${isExcluded ? "border-secondary opacity-50" : "border-primary"}`}
                                        >
                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={!isExcluded}
                                                        onChange={ev => updateProgram(idx, { excluded: !ev.target.checked })}
                                                        title="Include this sheet in the import"
                                                    />
                                                    <strong>Sheet {idx + 1}</strong>
                                                    {prog.is_remnant && (
                                                        <span className="badge bg-warning text-dark">Remnant</span>
                                                    )}
                                                    {prog.sheet_type && (
                                                        <span className="badge bg-light text-dark border">
                                                            {prog.sheet_type}
                                                        </span>
                                                    )}
                                                    <span className="text-muted small">
                                                        (pages {(prog.page_numbers || []).join(", ")})
                                                    </span>
                                                </div>
                                                <div className="text-muted small">
                                                    {prog.material_code} • <span title="Amada X x Y: X runs with the grain (JobQ length)">{prog.sheet_length}"×{prog.sheet_width}" (X×Y)</span>
                                                    {prog.thickness ? ` • ${prog.thickness}" thick` : ""}
                                                </div>
                                            </div>

                                            <div className="card-body">
                                                <div className="row g-3">
                                                    {/* LEFT: name + material + images */}
                                                    <div className="col-md-6">
                                                        <div className="mb-2">
                                                            <label className="form-label small fw-bold mb-1">
                                                                Cut Sheet Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className={`form-control form-control-sm ${!e.cut_sheet_name ? "is-invalid" : (conflictForProgram(idx) ? "border-warning" : "")}`}
                                                                value={e.cut_sheet_name || ""}
                                                                onChange={ev => updateProgram(idx, { cut_sheet_name: ev.target.value })}
                                                                disabled={isExcluded}
                                                            />
                                                            {/* Duplicate-name warning + create-revision opt-in */}
                                                            {!isExcluded && conflictForProgram(idx) && (
                                                                <div className={`alert ${e.reviseExisting ? "alert-info" : "alert-warning"} py-2 px-2 mt-2 mb-0 small`}>
                                                                    <div className="fw-bold mb-1">
                                                                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                                                        This cut sheet already exists
                                                                        {conflictForProgram(idx).existing_cut_sheet_name
                                                                            && conflictForProgram(idx).existing_cut_sheet_name !== (e.cut_sheet_name || "").trim()
                                                                            ? <> as "{conflictForProgram(idx).existing_cut_sheet_name}" — names are case-insensitive</>
                                                                            : null} (Rev {conflictForProgram(idx).current_revision_code}).
                                                                    </div>
                                                                    <div className="form-check mb-1">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`revise-${idx}`}
                                                                            checked={!!e.reviseExisting}
                                                                            onChange={ev => updateProgram(idx, { reviseExisting: ev.target.checked })}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`revise-${idx}`}>
                                                                            Import as <strong>Rev {conflictForProgram(idx).next_revision_code}</strong> from this upload
                                                                            {" "}(archives Rev {conflictForProgram(idx).current_revision_code})
                                                                            {conflictForProgram(idx).existing_cut_sheet_name
                                                                                && conflictForProgram(idx).existing_cut_sheet_name !== (e.cut_sheet_name || "").trim()
                                                                                ? <span className="text-muted"> — keeps the name "{conflictForProgram(idx).existing_cut_sheet_name}"</span>
                                                                                : null}
                                                                        </label>
                                                                    </div>
                                                                    {!e.reviseExisting && (
                                                                        <div className="text-muted">
                                                                            Or rename above to create a separate cut sheet.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label small fw-bold mb-1">
                                                                Material
                                                            </label>
                                                            <select
                                                                className={`form-select form-select-sm ${!e.material_id ? "is-invalid" : ""}`}
                                                                value={e.material_id || ""}
                                                                onChange={ev => updateProgram(idx, { material_id: parseInt(ev.target.value) || null })}
                                                                disabled={isExcluded}
                                                            >
                                                                <option value="">-- Select material --</option>
                                                                {materialOptions.map(o => (
                                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                                ))}
                                                            </select>

                                                            {/* Candidate chips — only show top 3 */}
                                                            {prog.material_match_candidates && prog.material_match_candidates.length > 0 && (
                                                                <div className="mt-1">
                                                                    <small className="text-muted me-2">Parser suggested:</small>
                                                                    {prog.material_match_candidates.slice(0, 3).map((m, mIdx) => (
                                                                        <button
                                                                            key={mIdx}
                                                                            type="button"
                                                                            className={`btn btn-sm me-1 mb-1 py-0 px-2 ${e.material_id === m.material_id ? "btn-primary" : "btn-outline-secondary"}`}
                                                                            onClick={() => updateProgram(idx, { material_id: m.material_id })}
                                                                            disabled={isExcluded}
                                                                            title={`${m.match_reason} (${m.description})`}
                                                                        >
                                                                            <span className={`badge ${confidenceBadge(m.confidence)} me-1`}>
                                                                                {(m.confidence * 100).toFixed(0)}%
                                                                            </span>
                                                                            {m.material_code}
                                                                            {m.grain_turned && (
                                                                                <span className="badge bg-warning text-dark ms-1" title="Same size, but the sheet is turned 90 degrees: this material's grain runs across the program's X">
                                                                                    grain turned
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {(!prog.material_match_candidates || prog.material_match_candidates.length === 0) && (
                                                                <small className="text-danger">
                                                                    <i className="bi bi-x-circle me-1"></i>
                                                                    No material match in owner's catalog — pick manually.
                                                                </small>
                                                            )}
                                                        </div>

                                                        {/* Times */}
                                                        <div className="small text-muted">
                                                            Cut: {prog.cut_path_time} &nbsp;
                                                            SH: {prog.sh_cut_time} &nbsp;
                                                            Pierce: {prog.pierce_time} &nbsp;
                                                            <span className="fw-bold">
                                                                Total: {prog.total_cut_time}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* RIGHT: thumbnails */}
                                                    <div className="col-md-6">
                                                        {prog.data_name_image_url && (
                                                            <div className="text-center mb-2">
                                                                <img
                                                                    src={prog.data_name_image_url}
                                                                    alt="Data Name glyph"
                                                                    style={{ maxHeight: "32px", maxWidth: "100%" }}
                                                                />
                                                            </div>
                                                        )}
                                                        {prog.diagram_image_url && (
                                                            <div className="text-center">
                                                                <img
                                                                    src={prog.diagram_image_url}
                                                                    alt="Nesting diagram"
                                                                    style={{ maxHeight: "180px", maxWidth: "100%", border: "1px solid #ccc" }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Parts table */}
                                                <div className="mt-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <h6 className="small fw-bold text-uppercase text-muted mb-0">
                                                            Parts on this sheet
                                                        </h6>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => addCustomPart(idx)}
                                                            disabled={isExcluded}
                                                            title="Add a part the parser missed"
                                                        >
                                                            <i className="bi bi-plus-lg me-1"></i>Add Part
                                                        </button>
                                                    </div>
                                                    <table className="table table-sm table-bordered align-middle mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ width: "200px" }}>Amada Part No.</th>
                                                                <th className="text-center" style={{ width: "80px" }}>Qty/Sheet</th>
                                                                <th>JobQ Part (match)</th>
                                                                <th className="text-center" style={{ width: "80px" }}>Conf.</th>
                                                                <th className="text-center" style={{ width: "60px" }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {/* Parser-detected rows */}
                                                            {(prog.parts || []).map((p, pIdx) => {
                                                                const partEdit = (e.parts && e.parts[pIdx]) || {};
                                                                const removed = !!partEdit.removed;
                                                                const chosenPartId = partEdit.part_id || null;
                                                                const chosenCandidate = (p.match_candidates || []).find(c => c.part_id === chosenPartId);
                                                                const showConf = chosenCandidate ? chosenCandidate.confidence : 0;
                                                                const isExact = chosenCandidate && chosenCandidate.match_reason === "exact";
                                                                const editedQty = partEdit.quantity_per_sheet != null ? partEdit.quantity_per_sheet : p.qty_per_sheet;
                                                                const qtyNum = parseInt(editedQty, 10);
                                                                const qtyInvalid = chosenPartId && (!Number.isFinite(qtyNum) || qtyNum < 1);

                                                                if (removed) {
                                                                    return (
                                                                        <tr key={`pp-${pIdx}`} className="table-secondary text-muted">
                                                                            <td><code style={{ textDecoration: "line-through" }}>{p.product_id}</code></td>
                                                                            <td colSpan={3} className="fst-italic small">— removed —</td>
                                                                            <td className="text-center">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-secondary"
                                                                                    onClick={() => restoreProgramPart(idx, pIdx)}
                                                                                    disabled={isExcluded}
                                                                                    title="Restore"
                                                                                >
                                                                                    <i className="bi bi-arrow-counterclockwise"></i>
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                }

                                                                const rowClass = !chosenPartId
                                                                    ? "table-danger"
                                                                    : qtyInvalid
                                                                        ? "table-danger"
                                                                        : (isExact ? "" : "table-warning");
                                                                return (
                                                                    <tr key={`pp-${pIdx}`} className={rowClass}>
                                                                        <td><code>{p.product_id}</code></td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="number"
                                                                                min="1"
                                                                                step="1"
                                                                                className={`form-control form-control-sm text-center ${qtyInvalid ? "is-invalid" : ""}`}
                                                                                style={{ width: "70px", margin: "0 auto" }}
                                                                                value={editedQty == null ? "" : editedQty}
                                                                                onChange={ev => updateProgramPart(idx, pIdx, {
                                                                                    quantity_per_sheet: ev.target.value === "" ? "" : parseInt(ev.target.value, 10)
                                                                                })}
                                                                                disabled={isExcluded}
                                                                                title={p.qty_per_sheet ? `Parser read ${p.qty_per_sheet}` : "Parser couldn't read qty — enter manually"}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <Autocomplete
                                                                                size="small"
                                                                                options={partOptions}
                                                                                value={partOptions.find(o => o.value === chosenPartId) || null}
                                                                                onChange={(_ev, newVal) => updateProgramPart(idx, pIdx, { part_id: newVal ? newVal.value : null })}
                                                                                getOptionLabel={(option) => option?.label || ""}
                                                                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                                                disabled={isExcluded}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        placeholder="-- No match — type to search --"
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        error={!chosenPartId && !isExcluded}
                                                                                    />
                                                                                )}
                                                                                ListboxProps={{ style: { maxHeight: 280 } }}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {chosenPartId ? (
                                                                                <span className={`badge ${confidenceBadge(showConf)}`}>
                                                                                    {(showConf * 100).toFixed(0)}%
                                                                                </span>
                                                                            ) : (
                                                                                <span className="badge bg-danger">none</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => removeProgramPart(idx, pIdx)}
                                                                                disabled={isExcluded}
                                                                                title="Remove this part from the import"
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}

                                                            {/* Operator-added custom rows */}
                                                            {(e.customParts || []).map((cp, cIdx) => {
                                                                const chosenPartId = cp.part_id || null;
                                                                const editedQty = cp.quantity_per_sheet;
                                                                const qtyNum = parseInt(editedQty, 10);
                                                                const qtyInvalid = chosenPartId && (!Number.isFinite(qtyNum) || qtyNum < 1);
                                                                const rowClass = !chosenPartId
                                                                    ? "table-danger"
                                                                    : qtyInvalid
                                                                        ? "table-danger"
                                                                        : "table-info";
                                                                return (
                                                                    <tr key={`cp-${cIdx}`} className={rowClass}>
                                                                        <td>
                                                                            <span className="badge bg-info text-dark">
                                                                                <i className="bi bi-plus-lg me-1"></i>Manual
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="number"
                                                                                min="1"
                                                                                step="1"
                                                                                className={`form-control form-control-sm text-center ${qtyInvalid ? "is-invalid" : ""}`}
                                                                                style={{ width: "70px", margin: "0 auto" }}
                                                                                value={editedQty == null ? "" : editedQty}
                                                                                onChange={ev => updateCustomPart(idx, cIdx, {
                                                                                    quantity_per_sheet: ev.target.value === "" ? "" : parseInt(ev.target.value, 10)
                                                                                })}
                                                                                disabled={isExcluded}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <Autocomplete
                                                                                size="small"
                                                                                options={partOptions}
                                                                                value={partOptions.find(o => o.value === chosenPartId) || null}
                                                                                onChange={(_ev, newVal) => updateCustomPart(idx, cIdx, { part_id: newVal ? newVal.value : null })}
                                                                                getOptionLabel={(option) => option?.label || ""}
                                                                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                                                disabled={isExcluded}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        placeholder="Type to search parts…"
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        error={!chosenPartId && !isExcluded}
                                                                                    />
                                                                                )}
                                                                                ListboxProps={{ style: { maxHeight: 280 } }}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center text-muted">—</td>
                                                                        <td className="text-center">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => removeCustomPart(idx, cIdx)}
                                                                                disabled={isExcluded}
                                                                                title="Remove this manual row"
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Non-inventory Remnants */}
                                                <div className="mt-3">
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="small fw-bold text-uppercase text-muted mb-0 d-flex align-items-center">
                                                            Non-inventory Remnants
                                                            {(e.remnants || []).some(r => r.detected) ? (
                                                                <span className="badge bg-info ms-2" style={{ fontSize: "0.65rem" }}>
                                                                    <i className="bi bi-magic me-1"></i>Detected
                                                                </span>
                                                            ) : null}
                                                        </h6>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => updateProgram(idx, {
                                                                remnants: [...(e.remnants || []), { width: "", length: "", quantity: "1", detected: false }],
                                                            })}
                                                            disabled={isExcluded}
                                                            title="Add another remnant size"
                                                        >
                                                            <i className="bi bi-plus-lg me-1"></i>Add remnant
                                                        </button>
                                                    </div>
                                                    {(e.remnants || []).length === 0 && (
                                                        <small className="text-muted fst-italic d-block">
                                                            No remnants — click "Add remnant" if this sheet leaves a leftover piece.
                                                        </small>
                                                    )}
                                                    {(() => {
                                                        // The chosen material matches this program only turned 90 degrees: Amada's X
                                                        // runs across that material's grain, so the remnants read X -> length have
                                                        // their grain turned too (review 2026-10-25).
                                                        const chosen = (prog.material_match_candidates || []).find(m => m.material_id === e.material_id);
                                                        if (!chosen || !chosen.grain_turned || !(e.remnants || []).length) return null;
                                                        return (
                                                            <div className="alert alert-warning py-1 px-2 small mb-2">
                                                                {chosen.material_code} is this program's sheet turned 90 degrees, so Amada's X runs
                                                                across its grain. The remnants below were read X as length (with the grain).
                                                                If the material's sizes are right, swap them.
                                                                <button type="button" className="btn btn-sm btn-warning ms-2 py-0" disabled={isExcluded}
                                                                    onClick={() => updateProgram(idx, {
                                                                        remnants: e.remnants.map(it => ({ ...it, width: it.length, length: it.width, detected: false })),
                                                                    })}>
                                                                    Swap all remnants
                                                                </button>
                                                            </div>
                                                        );
                                                    })()}
                                                    {(e.remnants || []).map((r, rIdx) => (
                                                        <div key={rIdx} className="row g-2 align-items-end mb-2">
                                                            <div className="col-auto" style={{ minWidth: "140px" }}>
                                                                {rIdx === 0 && <label className="form-label small mb-0 text-muted" title="Across the grain, Amada Y">Width (in, across grain)</label>}
                                                                <input
                                                                    type="number" step="0.0001" min="0"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="—"
                                                                    value={r.width == null ? "" : r.width}
                                                                    onChange={ev => updateProgram(idx, {
                                                                        remnants: e.remnants.map((it, i) => i === rIdx ? { ...it, width: ev.target.value, detected: false } : it),
                                                                    })}
                                                                    disabled={isExcluded}
                                                                />
                                                            </div>
                                                            <div className="col-auto" style={{ minWidth: "140px" }}>
                                                                {rIdx === 0 && <label className="form-label small mb-0 text-muted" title="With the grain, Amada X: the first number in Amada's Remnant info">Length (in, with grain)</label>}
                                                                <input
                                                                    type="number" step="0.0001" min="0"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="—"
                                                                    value={r.length == null ? "" : r.length}
                                                                    onChange={ev => updateProgram(idx, {
                                                                        remnants: e.remnants.map((it, i) => i === rIdx ? { ...it, length: ev.target.value, detected: false } : it),
                                                                    })}
                                                                    disabled={isExcluded}
                                                                />
                                                            </div>
                                                            <div className="col-auto" style={{ minWidth: "110px" }}>
                                                                {rIdx === 0 && <label className="form-label small mb-0 text-muted">Qty per sheet</label>}
                                                                <input
                                                                    type="number" step="1" min="1"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="—"
                                                                    value={r.quantity == null ? "" : r.quantity}
                                                                    onChange={ev => updateProgram(idx, {
                                                                        remnants: e.remnants.map((it, i) => i === rIdx ? { ...it, quantity: ev.target.value, detected: false } : it),
                                                                    })}
                                                                    disabled={isExcluded}
                                                                />
                                                            </div>
                                                            {r.width && r.length ? (
                                                                <div className="col-auto small text-muted pb-1" title="Amada lists X (with the grain) first">
                                                                    Amada {amadaSizeText(r.width, r.length)}
                                                                </div>
                                                            ) : null}
                                                            <div className="col-auto">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => updateProgram(idx, {
                                                                        remnants: e.remnants.filter((_it, i) => i !== rIdx),
                                                                    })}
                                                                    disabled={isExcluded}
                                                                    title="Remove this remnant"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            </div>
                                                            {r.detected && (
                                                                <div className="col">
                                                                    <small className="text-info">
                                                                        <i className="bi bi-magic me-1"></i>From PDF
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {(e.remnants || []).length > 0 && (
                                                        <small className="text-muted">
                                                            Each remnant is auto-added to inventory every time this sheet is cut.
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Dry-run result */}
                                {dryRunOk && (
                                    <div className="alert alert-success">
                                        <i className="bi bi-check-circle me-2"></i>
                                        Dry run passed — all validations OK. Click <strong>Import</strong> to commit.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ======================== IMPORTING STEP ======================== */}
                        {step === "importing" && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-success mb-3"></div>
                                <div>Creating cut sheets…</div>
                            </div>
                        )}

                        {/* ======================== DONE STEP ======================== */}
                        {step === "done" && (
                            <div>
                                <div className="alert alert-success">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    <strong>Import complete.</strong>{" "}
                                    {importResult?.message || `Created ${createdIds.length} cut sheet${createdIds.length === 1 ? "" : "s"}.`}
                                </div>
                                {importResult && importResult.revisions && importResult.revisions.length > 0 && (
                                    <div className="alert alert-info small">
                                        <div className="fw-bold mb-1">
                                            <i className="bi bi-arrow-repeat me-1"></i>
                                            Revisions created:
                                        </div>
                                        <ul className="mb-0">
                                            {importResult.revisions.map((r, rIdx) => (
                                                <li key={rIdx}>
                                                    <code>{r.cut_sheet_name}</code> — Rev {r.from_revision} → <strong>Rev {r.to_revision}</strong>
                                                    {" "}(old revision archived)
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {mapResults === "saving" && (
                                    <div className="small text-muted mb-2">Saving the sheet maps for the laser screen…</div>
                                )}
                                {Array.isArray(mapResults) && mapResults.length > 0 && (
                                    <div className={`small mb-2 ${mapResults.every(r => r.ok) ? "text-success" : "text-warning"}`}
                                         data-testid="import-sheet-maps">
                                        {mapsSummary(mapResults)}
                                        {mapResults.filter(r => !r.ok || (r.problems || []).length).map(r => (
                                            <div key={r.cutSheetId}>
                                                Cut sheet {r.cutSheetId}: {r.ok ? r.problems.join("; ") : r.message}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {createdIds.length > 0 && (
                                    <ul className="small">
                                        {createdIds.map(id => (
                                            <li key={id}>Cut sheet ID <code>{id}</code></li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        {step === "upload" && (
                            <>
                                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleParse}
                                    disabled={!ownerCustomerId || !cutSheetCustomerId || !pdfFile}
                                >
                                    <i className="bi bi-upload me-1"></i>
                                    Parse PDF
                                </button>
                            </>
                        )}

                        {step === "parsing" && (
                            <button className="btn btn-secondary" disabled>Parsing…</button>
                        )}

                        {step === "review" && (
                            <>
                                <button className="btn btn-outline-secondary" onClick={() => setStep("upload")}>
                                    <i className="bi bi-arrow-left me-1"></i> Back
                                </button>
                                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                                <button
                                    className="btn btn-warning"
                                    onClick={handleDryRun}
                                    disabled={!readyToImport}
                                    title="Validate without committing"
                                >
                                    <i className="bi bi-play-circle me-1"></i>
                                    Dry Run
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleImport}
                                    disabled={!readyToImport || !dryRunOk}
                                    title={!dryRunOk ? "Run Dry Run first to validate" : "Commit all included cut sheets"}
                                >
                                    <i className="bi bi-check-lg me-1"></i>
                                    Import
                                </button>
                            </>
                        )}

                        {step === "importing" && (
                            <button className="btn btn-secondary" disabled>Importing…</button>
                        )}

                        {step === "done" && (
                            <button className="btn btn-primary" onClick={onClose}>Close</button>
                        )}

                        {step === "error" && (
                            <>
                                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                                <button className="btn btn-primary" onClick={() => setStep("upload")}>
                                    <i className="bi bi-arrow-left me-1"></i> Try Again
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
